/**
 * GoogleAnalytics.tsx — GA4 com carregamento condicional por consent (React).
 *
 * NÃO injeta o script do gtag até o usuário aceitar cookies.
 * Escuta 3 fontes:
 *   1. localStorage ao montar (usuário já decidiu anteriormente)
 *   2. CustomEvent 'drpera-consent-change' (decisão na mesma sessão)
 *   3. StorageEvent 'storage' (sincronização entre abas)
 *
 * anonymize_ip: true — conformidade LGPD/GDPR.
 *
 * Uso: adicionar <GoogleAnalytics /> no root do App.tsx.
 */
import { useEffect } from 'react';
import { getConsent, CONSENT_KEY } from '../utils/cookieConsent';

/** Tipos globais do gtag para TypeScript */
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __ga4_loaded?: boolean;
  }
}

export function GoogleAnalytics(): null {
  useEffect(() => {
    const ga4Id = import.meta.env.VITE_GA4_MEASUREMENT_ID ?? '';
    if (!ga4Id) return;

    /**
     * Injeta o script do gtag.js e configura GA4.
     * Flag __ga4_loaded evita carregamento duplo.
     */
    function carregarGA4(): void {
      if (window.__ga4_loaded) return;
      window.__ga4_loaded = true;

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function(...args: unknown[]) {
        window.dataLayer!.push(args);
      };

      window.gtag('js', new Date());
      window.gtag('config', ga4Id, {
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure',
      });
    }

    /* Verificação imediata — usuário já decidiu antes */
    if (getConsent() === 'granted') {
      carregarGA4();
      return;
    }

    /* Escuta decisão do banner na mesma aba */
    const handleConsentChange = (e: Event): void => {
      const evento = e as CustomEvent<string>;
      if (evento.detail === 'granted') carregarGA4();
    };

    /* Sincronização entre abas abertas */
    const handleStorage = (e: StorageEvent): void => {
      if (e.key === CONSENT_KEY && e.newValue === 'granted') carregarGA4();
    };

    window.addEventListener('drpera-consent-change', handleConsentChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('drpera-consent-change', handleConsentChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return null;
}
